import { useState, useEffect, useRef } from "react";
import { Check, X, Loader2, AlertCircle } from "lucide-react";
import { useCheckUsername } from "../../hooks/profileQueries";

export default function UsernameInput({ value, onChange, originalUsername }) {
  const [checkStatus, setCheckStatus] = useState(null); // null | 'checking' | 'available' | 'taken' | 'invalid'
  const [message, setMessage] = useState('');
  const debounceTimer = useRef(null);
  const checkUsernameMutation = useCheckUsername();

  useEffect(() => {
    // Clear any existing timer
    if (debounceTimer.current) {
      clearTimeout(debounceTimer.current);
    }

    // Reset if empty or same as original
    if (!value || value === originalUsername) {
      setCheckStatus(null);
      setMessage('');
      return;
    }

    // Set checking status
    setCheckStatus('checking');
    setMessage('Checking availability...');

    // Debounce the API call
    debounceTimer.current = setTimeout(async () => {
      try {
        const result = await checkUsernameMutation.mutateAsync(value);
        
        if (result.available) {
          setCheckStatus('available');
          setMessage('Username is available');
        } else {
          setCheckStatus(result.message.includes('characters') || 
                        result.message.includes('contain') ? 'invalid' : 'taken');
          setMessage(result.message);
        }
      } catch (error) {
        setCheckStatus('invalid');
        setMessage('Error checking username');
      }
    }, 350); 

    // Cleanup
    return () => {
      if (debounceTimer.current) {
        clearTimeout(debounceTimer.current);
      }
    };
  }, [value, originalUsername]);

  const getStatusIcon = () => {
    switch (checkStatus) {
      case 'checking':
        return <Loader2 className="w-4 h-4 text-gray-400 animate-spin" />;
      case 'available':
        return <Check className="w-4 h-4 text-green-500" />;
      case 'taken':
        return <X className="w-4 h-4 text-red-500" />;
      case 'invalid':
        return <AlertCircle className="w-4 h-4 text-orange-500" />;
      default:
        return null;
    }
  };

  const getStatusColor = () => {
    switch (checkStatus) {
      case 'checking':
        return 'border-gray-200 focus:ring-gray-500';
      case 'available':
        return 'border-green-300 focus:ring-green-500';
      case 'taken':
      case 'invalid':
        return 'border-red-300 focus:ring-red-500';
      default:
        return 'border-gray-200 focus:ring-blue-500';
    }
  };

  const getMessageColor = () => {
    switch (checkStatus) {
      case 'checking':
        return 'text-gray-500';
      case 'available':
        return 'text-green-600';
      case 'taken':
      case 'invalid':
        return 'text-red-600';
      default:
        return 'text-gray-500';
    }
  };

  return (
    <div className="space-y-1 sm:space-y-2">
      <label
        htmlFor="username"
        className="block text-xs sm:text-sm font-semibold text-gray-700"
      >
        Username
      </label>
      <div className="relative">
        <input
          type="text"
          name="username"
          id="username"
          value={value}
          onChange={onChange}
          className={`block w-full px-3 sm:px-4 py-2 sm:py-3 text-xs sm:text-sm border rounded-lg sm:rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-300 bg-gray-50 focus:bg-white ${getStatusColor()}`}
          placeholder="Your unique username"
        />
        <div className="absolute inset-y-0 right-0 pr-3 sm:pr-4 flex items-center pointer-events-none">
          {getStatusIcon()}
        </div>
      </div>
      {checkStatus && message && (
        <p className={`text-xs ${getMessageColor()} flex items-center gap-1 animate-in fade-in duration-200`}>
          {message}
        </p>
      )}
    </div>
  );
}